/**
 * Persistence for the user's GDPR cookie/analytics consent choice.
 * Mirrors the defensive try/catch style of `utils/localStorageHelper.js`.
 *
 * Stored value is one of: "granted" | "denied" | null (no choice yet).
 * A null/absent value means the banner should be shown.
 *
 * Persistence layers:
 *   - localStorage (always) — the original, per-ORIGIN store.
 *   - domain-wide cookie (ONLY when CONSENT_UX_FIX_ENABLED) — scoped to the
 *     registrable domain (`.iyuga.net`) so a single Accept/Reject suppresses the
 *     banner across EVERY iYuga subdomain. localStorage is per-origin, so a
 *     choice made on one host (web-stg) never carried to another (app-stg / www)
 *     and the banner re-showed on each. The cookie also lets a choice persist
 *     when localStorage is blocked. Reads prefer the cookie, then fall back to
 *     localStorage (so choices made before this fix are still honoured).
 *
 * When CONSENT_UX_FIX_ENABLED is OFF, this module behaves BYTE-IDENTICALLY to
 * the original (localStorage only — the cookie branches are never entered).
 */
import { CONSENT_UX_FIX_ENABLED } from "../constants/featureFlags";

export const CONSENT_STORAGE_KEY = "iyuga_analytics_consent";
export const CONSENT_GRANTED = "granted";
export const CONSENT_DENIED = "denied";

// GDPR re-prompt window: re-ask after 180 days (6 months) — a common, defensible
// consent lifetime (ICO/CNIL guidance sits at 6–13 months).
const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 180;

/**
 * The cookie Domain attribute so the consent choice is shared across every
 * iYuga subdomain. Returns ".iyuga.net" on any *.iyuga.net host; undefined
 * elsewhere (localhost, PR previews) so the cookie stays host-only there.
 * Never throws.
 */
function consentCookieDomain() {
  try {
    const host = window.location.hostname || "";
    if (host === "iyuga.net" || host.endsWith(".iyuga.net")) {
      return ".iyuga.net";
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
}

function readConsentCookie() {
  try {
    if (typeof document === "undefined" || !document.cookie) {
      return null;
    }
    const parts = document.cookie.split(";");
    for (let i = 0; i < parts.length; i += 1) {
      const [rawKey, ...rest] = parts[i].split("=");
      if (rawKey && rawKey.trim() === CONSENT_STORAGE_KEY) {
        return decodeURIComponent(rest.join("=").trim());
      }
    }
    return null;
  } catch (e) {
    return null;
  }
}

function writeConsentCookie(value) {
  try {
    if (typeof document === "undefined") {
      return false;
    }
    const domain = consentCookieDomain();
    const secure =
      typeof window !== "undefined" &&
      window.location &&
      window.location.protocol === "https:"
        ? "; Secure"
        : "";
    document.cookie =
      `${CONSENT_STORAGE_KEY}=${encodeURIComponent(value)}` +
      `; path=/; max-age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax` +
      (domain ? `; domain=${domain}` : "") +
      secure;
    return true;
  } catch (e) {
    return false;
  }
}

export function getStoredConsent() {
  try {
    // Fix ON: a domain-wide cookie is the source of truth (cross-subdomain).
    if (CONSENT_UX_FIX_ENABLED) {
      const cookieValue = readConsentCookie();
      if (cookieValue === CONSENT_GRANTED || cookieValue === CONSENT_DENIED) {
        return cookieValue;
      }
    }
    if (typeof window === "undefined" || !window.localStorage) {
      return null;
    }
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (value === CONSENT_GRANTED || value === CONSENT_DENIED) {
      return value;
    }
    return null;
  } catch (e) {
    // If storage is blocked (private mode, etc.), behave as "no choice yet".
    return null;
  }
}

export function setStoredConsent(value) {
  try {
    if (value !== CONSENT_GRANTED && value !== CONSENT_DENIED) {
      return false;
    }
    let persisted = false;
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
      persisted = true;
    }
    // Fix ON: also write the domain-wide cookie so the choice carries across
    // iYuga subdomains (and survives a blocked/absent localStorage).
    if (CONSENT_UX_FIX_ENABLED && writeConsentCookie(value)) {
      persisted = true;
    }
    return persisted;
  } catch (e) {
    return false;
  }
}
